import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { color, tooltipStyle, legendStyle } from "./palette";
import { GlassDefs, glassFill, GLASS_STROKE } from "./GlassDefs";
import { useNarrow } from "../../hooks/use-narrow";

function Panel({ title, subtitle, children, testid, actions }) {
  return (
    <section className="glass-panel p-5" data-testid={testid}>
      <div className="flex items-baseline justify-between mb-4 gap-3">
        <div>
          <h3 className="font-display font-bold text-lg tracking-tight">{title}</h3>
          {subtitle && (
            <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-500 mt-0.5">
              {subtitle}
            </p>
          )}
        </div>
        {actions}
      </div>
      {children}
    </section>
  );
}

/**
 * Reusable colourful bar chart card.
 * Props:
 *   title, subtitle, testid, actions   — panel chrome
 *   data: [{name, value}]              for single-series; one colour per Cell from palette
 *   height: tailwind class height (default "h-72")
 *   orientation: "vertical" (default) | "horizontal" (categories on Y-axis)
 *   stacked: when provided as {keys:[...]}  renders stacked bars (one colour per key)
 *   xAxisAngle: tilt X labels (e.g. -30); default 0
 *   emptyText: shown when data is empty
 */
export default function BarChartCard({
  title,
  subtitle,
  testid,
  actions,
  data,
  height = "h-60 sm:h-72",
  orientation = "vertical",
  stacked,
  xAxisAngle = 0,
  emptyText = "No data yet",
}) {
  const narrow = useNarrow();
  const hasData = Array.isArray(data) && data.length > 0;
  const isHorizontal = orientation === "horizontal";
  const isStacked = !!(stacked && Array.isArray(stacked.keys) && stacked.keys.length);

  return (
    <Panel title={title} subtitle={subtitle} testid={testid} actions={actions}>
      <div className={height}>
        {!hasData ? (
          <div className="h-full flex items-center justify-center text-sm text-neutral-500 font-mono uppercase tracking-wider">
            {emptyText}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              layout={isHorizontal ? "vertical" : "horizontal"}
              margin={{
                top: 5,
                right: 16,
                left: isHorizontal ? (narrow ? 8 : 80) : 0,
                bottom: xAxisAngle ? 60 : 5,
              }}
            >
              <GlassDefs />
              <CartesianGrid
                stroke="rgba(100,116,139,0.18)"
                strokeDasharray="3 3"
                vertical={isHorizontal}
                horizontal={!isHorizontal}
              />
              {isHorizontal ? (
                <>
                  <XAxis
                    type="number"
                    allowDecimals={false}
                    tick={{ fontSize: 11, fontFamily: "Inter" }}
                    stroke="rgba(100,116,139,0.35)" axisLine={false} tickLine={false}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={narrow ? 92 : 160}
                    tick={{ fontSize: narrow ? 10 : 11 }}
                    stroke="rgba(100,116,139,0.35)" axisLine={false} tickLine={false}
                  />
                </>
              ) : (
                <>
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 10 }}
                    interval={0}
                    angle={xAxisAngle}
                    textAnchor={xAxisAngle ? "end" : "middle"}
                    height={xAxisAngle ? 70 : 30}
                    stroke="rgba(100,116,139,0.35)" axisLine={false} tickLine={false}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fontSize: 11, fontFamily: "Inter" }}
                    stroke="rgba(100,116,139,0.35)" axisLine={false} tickLine={false}
                  />
                </>
              )}
              <Tooltip contentStyle={tooltipStyle} />
              {isStacked ? (
                <>
                  <Legend wrapperStyle={legendStyle} />
                  {stacked.keys.map((k, i) => (
                    <Bar key={k} dataKey={k} stackId="a" fill={glassFill(i)} stroke={GLASS_STROKE} strokeWidth={1} />
                  ))}
                </>
              ) : (
                <Bar dataKey="value" radius={isHorizontal ? [0, 8, 8, 0] : [8, 8, 0, 0]}>
                  {data.map((entry, i) => (
                    <Cell key={entry.name} fill={glassFill(i, isHorizontal)} stroke={GLASS_STROKE} strokeWidth={1} />
                  ))}
                </Bar>
              )}
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </Panel>
  );
}
